import { Button, Grid, Checkbox, FormControlLabel, makeStyles } from "@material-ui/core";
import Authorize from "components/Authorize";
import { isAuthorized } from "components/Authorize/authorizeUtil";
import { invoicePermissionProfile } from "components/Authorize/permissionProfiles/invoicePermissionProfile";
import DataTable from "components/DataTable/DataTable";
import ExportButton from "components/ExportButton/ExportButton";
import LoadingSpinner from "components/LoadingSpinner/LoadingSpinner";
import NotificationProvider from "components/NotificationProvider";
import { userPreferencesComponentIds } from "components/UserPreferences/userPreferencesConsts";
import { useGetInvoiceVendorsQuery } from 'features/invoice/loads/loadInvoiceSlice';
import usePrompt from "hooks/usePrompt";
import useSortOptions from 'hooks/useSortOptions';
import { IconDeleteBlue, IconEdit } from "icons";
import { orderBy } from "lodash";
import { useState, useMemo } from "react";
import FilterService from "services/filterService";
import FormatService from "services/formatService";
import VendorInvoiceForm from "./components/VendorInvoiceForm";
import VendorInvoicePreview from "./components/VendorInvoicePreview";
import { getVendorInvoiceColumns, VendorInvoiceStatusesList } from "./vendorInvoiceConsts";
import { useDeleteVendorInvoiceMutation, useGetVendorInvoicesQuery } from "./vendorInvoiceSlice";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useGetVendorsQuery } from "features/vendor/vendorSlice";
import { arrayToObject } from "utils/object-util";
import { fieldTypes } from "services/filterService";
import { useDispatch, useSelector } from "react-redux";
import { setFilters, setSearchVal } from "./vendorInvoiceFiltersSlice";

const VendorInvoicePage = () => {
    const classes = useStyles();
    
    const dispatch = useDispatch();

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const invoiceId = searchParams.get("invoiceId");

    const { triggerPrompt } = usePrompt();

    const [includeHistory, setIncludeHistory] = useState(false);

    let { data: invoices, error: invoicesError, isLoading: isLoadingInvoices } = useGetVendorInvoicesQuery({ includeHistory });
    let { data: qbVendors, error: qbVendorsError, isLoading: isLoadingQbVendors } = useGetInvoiceVendorsQuery();
    let { data: vendors, error: vendorsError, isLoading: isLoadingVendors } = useGetVendorsQuery();

    const [deleteVendorInvoice] = useDeleteVendorInvoiceMutation();

    const [invoiceToAdd, setInvoiceToAdd] = useState(false);
    const [invoiceToEdit, setInvoiceToEdit] = useState(null);
    const [invoiceToPreview, setInvoiceToPreview] = useState(null);
    const [sortOptions, sortData] = useSortOptions();
    const { searchVal, filters } = useSelector(state => state.vendorInvoiceFilters);

    const vendorValues = (vendors || []).map(v => ({ ...v, id: v.name }));

    const filterConfig = [
        { type: fieldTypes.LIST, title: 'Status', key: 'statusId', values: VendorInvoiceStatusesList },
        {
            type: fieldTypes.LIST,
            title: 'Has Errors',
            key: 'hasErrors',
            values: [
                { id: 'true', name: 'Yes' },
                { id: 'false', name: 'No' }
            ]
        },
        { type: fieldTypes.LIST, title: 'Parq Vendor', key: 'parqVendorName', values: vendorValues }
    ];

    const onDropFilter = (filterValue, prop) => {
        dispatch(setFilters({
            ...filters,
            [prop]: { value: filterValue }
        }));
    };

    const onResetFilters = () => {
        dispatch(setFilters({
            statusId: { value: null },
            parqVendorName: { value: null },
            hasErrors: { value: null },
        }));
    };

    const updateAllFilters = (newFilters) => {
        dispatch(setFilters(newFilters));
    };

    const searchVendors = value => {
        dispatch(setSearchVal(value.trim()));
    }


    invoices = invoices || [];
    qbVendors = qbVendors || [];

    var isLoading = isLoadingInvoices || isLoadingVendors;

    useEffect(() => {
        if (!isLoading && invoiceId && invoices) {
            const invoicesLookup = arrayToObject(invoices);
            const invoice = invoicesLookup[invoiceId];
            if (invoice) {
                setInvoiceToEdit(invoice);
            }
        }
    }, [isLoading, invoiceId, invoices]);


    var vendorsLookup = vendors?.reduce((acc, vendor) => {
        acc[vendor.id] = vendor;
        return acc;
    }, {});

    var qbVendorsLookup = arrayToObject(qbVendors);
    var vendorsLookup = arrayToObject(vendors);

    var mappedInvoices = useMemo(() => invoices?.map(invoice => {
        const qbVendor = qbVendorsLookup[invoice.qbVendorId];
        const vendor = vendorsLookup[invoice.vendorId];
        return {
            ...invoice,
            statusId: invoice.status,
            statusName: VendorInvoiceStatusesList?.find(s => s.id == invoice.status)?.name,
            formattedDate: FormatService.formatDateNoConversion(invoice.invoiceDate),
            qbVendor,
            qbVendorName: qbVendor?.displayName,
            parqVendorName: vendor?.name,
            vehiclesWithParqId: invoice.vehicles?.map(v => ({
                ...v,
                id: v.vehicleId,
                descriptor: v.vin
            })),
            vehicleVins: invoice.vehicles?.map(v => v.vin).filter(Boolean).join(' '),
            hasErrors: invoice.errorList && invoice.errorList.length > 0 ? 'true' : 'false'
        };
    }), [invoices, qbVendorsLookup, vendorsLookup]);

    var filteredInvoices = FilterService.filter(filters, searchVal, mappedInvoices, filterConfig);

    const canEdit = isAuthorized(invoicePermissionProfile.EDIT_VENDOR_INVOICE);

    const vendorInvoiceColumns = getVendorInvoiceColumns({
        setInvoiceToPreview,
        setInvoiceToEdit,
        mappedInvoices,
        canEdit
    });

    const mapExportData = row => {
        const invoiceData = {};
        vendorInvoiceColumns.map(col => {
            if (col.name !== "Notes") {
                invoiceData[col.name] = row[col.exportKey ?? col.key];
            }
        });
        invoiceData['Assets'] = row?.vehiclesWithParqId?.map(a => a.vin || a.assetId)?.join(', ');
        invoiceData['Status'] = row?.statusName;
        invoiceData['Parq Vendor'] = row?.parqVendorName;
        invoiceData['QB Vendor'] = row?.qbVendorName;
        return invoiceData;
    };

    const handleDelete = async (invoiceId) => {
        triggerPrompt({
            title: "Delete Invoice",
            content: "Are you sure you want to delete this invoice?",
            onConfirm: async () => {
                let res = await deleteVendorInvoice(invoiceId);

                if (!res?.error) {
                    NotificationProvider.success('Invoice deleted successfully');
                } else {
                    NotificationProvider.error('Failed to delete invoice');
                }
            },
        });
    };

    const rowActions = (rowId) => {
        const canEdit = isAuthorized(invoicePermissionProfile.EDIT_VENDOR_INVOICE);
        if (!canEdit) return null;
        return [
            {
                icon: <IconEdit />,
                onClick: () => setInvoiceToEdit(mappedInvoices.find(invoice => invoice.id === rowId))
            },
            {
                icon: <IconDeleteBlue width={'16'} height={'16'} />,
                onClick: () => handleDelete(rowId)
            }
        ];
    };

    return (
        <Authorize profile={invoicePermissionProfile.VIEW_VENDOR_INVOICE}>
            {isLoading ? (
                <LoadingSpinner loading={isLoading} />
            ) : (
                <>
                    <div className={classes.dataTableWrapper}>
                        <div className={classes.dataTableScrollArea}>
                            <DataTable
                                noItemsMessage="No Invoices"
                        allowColumnCustomization
                        tableId={userPreferencesComponentIds.VENDOR_INVOICE_TABLE}
                        onSearch={searchVendors}
                        searchVal={searchVal}
                        columns={vendorInvoiceColumns}
                        data={filteredInvoices}
                        rows={orderBy(
                            filteredInvoices,
                            sortOptions.columnToSort,
                            sortOptions.sortDirection
                        )}
                        rowIdentifier="id"
                        onSort={sortData}
                        sortDirection={sortOptions.sortDirection}
                        columnToSort={sortOptions.columnToSort}
                        actions={rowActions}
                        title={`Vendor Invoices (${filteredInvoices?.length})`}
                        withFilterPanel={true}
                        filters={filters}
                        onDropFilter={onDropFilter}
                        updateAllFilters={updateAllFilters}
                        dropDownsConfig={filterConfig}
                        onResetFilters={onResetFilters}
                        actionButtons={
                            <Grid container spacing={1} alignItems="center">
                                <Grid item>
                                    <FormControlLabel
                                        control={<Checkbox checked={includeHistory}
                                            onChange={(e, value) => setIncludeHistory(value)} />}
                                        label="Include History" />
                                </Grid>
                                <Authorize profile={invoicePermissionProfile.EDIT_VENDOR_INVOICE} readonlyComponent={<></>}>
                                    <Grid item>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => setInvoiceToAdd(true)}
                                        >
                                            Create New Invoice
                                        </Button>
                                    </Grid>
                                </Authorize>
                                <Grid item>
                                    <ExportButton fileLabel="Export" data={filteredInvoices} mapExportData={mapExportData} className='' />
                                </Grid>
                            </Grid>
                        }
                    />
                </div>
            </div>
            <VendorInvoicePreview
                invoice={invoiceToPreview}
                onClose={() => {
                    setInvoiceToPreview(null);
                }}
            />
            <VendorInvoiceForm
                open={invoiceToAdd}
                onClose={() => {
                    setInvoiceToAdd(false);
                }}
            />
            <VendorInvoiceForm
                open={!!invoiceToEdit}
                onClose={() => {
                    setInvoiceToEdit(null);
                }}
                invoice={invoiceToEdit}
            />
                </>
            )}
        </Authorize>
    );
};

export default VendorInvoicePage;

const useStyles = makeStyles({
    dataTableWrapper: {
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 150px)',
    },
    dataTableScrollArea: {
        flex: 1,
        overflow: 'auto',
        minHeight: 0
    }
});
